import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "../cmps/homepage/navigation";
import { FooterSection } from "../cmps/homepage/footer-section";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";

const services = [
  {
    number: "01",
    title: "Contractor Supervision",
    description: "Independent oversight of every contractor on your project — no conflicts of interest."
  },
  {
    number: "02",
    title: "Milestone Verification",
    description: "Every milestone is documented, photographed, and approved before payment is released."
  },
  {
    number: "03",
    title: "Progress Reporting",
    description: "Weekly reports with clear KPIs, blockers, and forward plan delivered to your inbox."
  },
  {
    number: "04",
    title: "Risk Management",
    description: "Early detection of cost, schedule, and quality risks with mitigation plans."
  },
  {
    number: "05",
    title: "Quality Assurance",
    description: "On-site and remote inspections by domain experts with photographic evidence."
  },
  {
    number: "06",
    title: "Vendor Vetting",
    description: "Background, capability and reference checks before any contractor is onboarded."
  },
];

const industries = [
  "Construction", "Renovation", "IT & Software", "Manufacturing",
  "Marketing", "Logistics", "Engineering", "Interior Design",
  "Event Production", "Energy"
];

export default function ServicesPage() {
  useEffect(() => {
    document.title = "Services | MANAGE ME";
  }, []);

  return (
    <div className="homepage-root">
      <main className="relative min-h-screen overflow-x-hidden">
        <Navigation />

        <section className="relative py-24 md:py-32 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-flex items-center gap-3 text-sm font-mono text-foreground/60 mb-8">
                <span className="w-8 h-px bg-foreground/30" />
                Services
              </span>
            </div>

            <h1 className="text-6xl md:text-7xl lg:text-8xl font-display tracking-tight leading-[0.9] mb-8">
              Independent oversight,
              <br />
              <span className="text-foreground/40">end to end.</span>
            </h1>

            <p className="text-xl text-foreground/70 leading-relaxed max-w-3xl">
              Pick the services you need. Bundle them into a plan. Or let us scope a custom engagement.
            </p>
          </div>
        </section>

        <section className="relative py-24 px-4 border-t border-foreground/10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {services.map((service) => (
                <div key={service.number} className="group">
                  <div className="mb-6">
                    <span className="text-5xl md:text-6xl font-display text-foreground/20 group-hover:text-foreground/40 transition-colors">
                      {service.number}
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-display mb-4">{service.title}</h3>
                  <p className="text-lg text-foreground/60 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-24 px-4 bg-foreground/5">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-display mb-16">Industries we serve</h2>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
              {industries.map((industry) => (
                <div
                  key={industry}
                  className="p-6 border border-foreground/10 rounded-lg hover:bg-foreground/5 transition-colors"
                >
                  <p className="font-medium text-foreground/80">{industry}</p>
                </div>
              ))}
            </div>

            <Link to="/contact">
              <Button
                size="lg"
                className="bg-foreground hover:bg-foreground/90 text-background px-8 h-14 text-base rounded-full group"
              >
                Scope your engagement
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </section>

        <FooterSection />
      </main>
    </div>
  );
}
